import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { DataTable } from '@apps/web/components/ui/data_table';
import { Spinner } from '@apps/web/components/ui/spinner';
import { type ColumnDef } from '@apps/web/lib/table';
import {
    matrixArms,
    type MatrixArmSummary,
    resolveCaseCostUsd,
    resolveMeanTtftMs,
    resolveRunCosts,
    resolveUnknownCostArmCount,
} from '@apps/web/routes/_app/admin/voice-evals/matrix_summary';
import {
    CaseStatusBadge,
    EvidenceFamilyBadge,
    EXECUTION_BACKEND_LABELS,
    EXECUTION_STATUS_LABELS,
    FlakyChip,
    formatDateTime,
    formatLatencyMs,
    formatPercent,
    formatUsd,
    RunOutcomeBadge,
    RunProvenanceCard,
    ttftMetricLabel,
} from '@apps/web/routes/_app/admin/voice-evals/shared';
import { useVoiceEvalRun } from '@apps/web/swr_data_fetching/admin_voice_eval_queries';
import { type EvalCaseResultListItem } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/voice-evals/runs/$runId/')({
    head: () => ({
        meta: [{ title: 'Eval Run | Phoebe Admin' }],
    }),
    component: VoiceEvalRunDetailPage,
});

function SummaryStat({
    label,
    value,
    description,
}: {
    label: string;
    value: string;
    description?: string;
}) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">
                    {label}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-semibold">{value}</div>
                {description && (
                    <p className="text-muted-foreground mt-1 text-xs">
                        {description}
                    </p>
                )}
            </CardContent>
        </Card>
    );
}

function MatrixResultsTable({ arms }: { arms: Array<MatrixArmSummary> }) {
    if (arms.length === 0) {
        return null;
    }
    return (
        <Card>
            <CardHeader>
                <CardTitle>Prompt-only model matrix</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
                <table className="w-full min-w-[880px] text-sm">
                    <thead className="border-b text-left">
                        <tr>
                            <th className="pb-2 font-medium">Phase</th>
                            <th className="pb-2 font-medium">Arm</th>
                            <th className="pb-2 font-medium">Model</th>
                            <th className="pb-2 text-right font-medium">
                                Passed
                            </th>
                            <th className="pb-2 text-right font-medium">
                                Known estimate
                            </th>
                            <th className="pb-2 text-right font-medium">
                                Tokens
                            </th>
                            <th className="pb-2 text-right font-medium">
                                Mean TTFT
                            </th>
                            <th className="pb-2 text-right font-medium">
                                p50 TTFT
                            </th>
                            <th className="pb-2 text-right font-medium">
                                p95 TTFT
                            </th>
                            <th className="pb-2 text-right font-medium">
                                Eval time
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {arms.map((arm) => (
                            <tr
                                key={`${arm.phase}/${arm.arm}`}
                                className="border-b last:border-0"
                            >
                                <td className="py-2">{arm.phase}</td>
                                <td className="py-2 font-mono text-xs">
                                    {arm.arm}
                                </td>
                                <td className="py-2">
                                    <div>{arm.model}</div>
                                    <div className="text-muted-foreground text-xs">
                                        {arm.provider} / {arm.reasoning_effort}
                                    </div>
                                </td>
                                <td className="py-2 text-right tabular-nums">
                                    {arm.total_passed}/{arm.total_attempted}
                                </td>
                                <td className="py-2 text-right tabular-nums">
                                    {arm.total_cost_usd === null
                                        ? arm.known_tracked_cost_usd !== null &&
                                          arm.known_tracked_cost_usd !==
                                              undefined
                                            ? `${formatUsd(Number(arm.known_tracked_cost_usd))} (partial)`
                                            : 'Unknown'
                                        : formatUsd(Number(arm.total_cost_usd))}
                                </td>
                                <td className="py-2 text-right tabular-nums">
                                    {(
                                        arm.prompt_tokens +
                                        arm.completion_tokens +
                                        arm.reasoning_tokens
                                    ).toLocaleString()}
                                </td>
                                <td className="py-2 text-right tabular-nums">
                                    {arm.ttft_sample_count === 0
                                        ? 'No answer token'
                                        : formatLatencyMs(arm.mean_ttft_ms)}
                                </td>
                                <td className="py-2 text-right tabular-nums">
                                    {arm.ttft_sample_count === 0
                                        ? 'No sample'
                                        : formatLatencyMs(arm.p50_ttft_ms)}
                                </td>
                                <td className="py-2 text-right tabular-nums">
                                    {arm.ttft_sample_count === 0
                                        ? 'No sample'
                                        : formatLatencyMs(arm.p95_ttft_ms)}
                                </td>
                                <td className="py-2 text-right tabular-nums">
                                    {arm.eval_elapsed_s === null
                                        ? '—'
                                        : `${arm.eval_elapsed_s.toFixed(1)}s`}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </CardContent>
        </Card>
    );
}

const caseColumns: Array<ColumnDef<EvalCaseResultListItem>> = [
    {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => <CaseStatusBadge caseResult={row.original} />,
    },
    {
        id: 'case_name',
        header: 'Case',
        cell: ({ row }) => (
            <Link
                to="/admin/voice-evals/cases"
                search={{ registry_id: row.original.registry_id }}
                onClick={(event) => event.stopPropagation()}
                className="block max-w-72 truncate font-mono text-xs underline-offset-2 hover:underline"
            >
                {row.original.case_name}
            </Link>
        ),
    },
    {
        id: 'use_case',
        header: 'Use case',
        cell: ({ row }) => (
            <span className="text-xs whitespace-nowrap">
                {row.original.use_case ?? '—'}
            </span>
        ),
    },
    {
        id: 'harness',
        header: 'Harness',
        cell: ({ row }) => (
            <span className="text-muted-foreground text-xs whitespace-nowrap">
                {row.original.harness ?? '—'}
            </span>
        ),
    },
    {
        id: 'score',
        header: 'Score',
        cell: ({ row }) =>
            row.original.score === null || row.original.score === undefined
                ? '—'
                : row.original.score.toFixed(2),
    },
    {
        id: 'latency',
        header: 'Full response',
        cell: ({ row }) => formatLatencyMs(row.original.avg_latency_ms),
    },
    {
        id: 'ttft',
        header: 'Answer TTFT',
        cell: ({ row }) => {
            if (typeof row.original.avg_time_to_first_token_ms === 'number') {
                return formatLatencyMs(row.original.avg_time_to_first_token_ms);
            }
            if (
                row.original.kind === 'prompt_only' &&
                row.original.attempted > 0
            ) {
                return 'No answer token';
            }
            return '—';
        },
    },
    {
        id: 'cost',
        header: 'Known estimate',
        cell: ({ row }) => (
            <span>
                {formatUsd(resolveCaseCostUsd(row.original))}
                {row.original.cost_tracking_complete === false && (
                    <span className="ml-1 text-xs text-amber-700">partial</span>
                )}
            </span>
        ),
    },
    {
        id: 'failures',
        header: 'First failure',
        cell: ({ row }) => {
            const failure = row.original.failure_summary?.[0];
            if (!failure) {
                return null;
            }
            return (
                <span
                    className="block max-w-96 truncate text-xs text-red-700"
                    title={failure}
                >
                    {failure}
                </span>
            );
        },
    },
];

function VoiceEvalRunDetailPage() {
    const router = useRouter();
    const { runId } = Route.useParams();
    const { data, isLoading, error } = useVoiceEvalRun(runId, {
        refreshInterval: 5_000,
        pollUntilTerminal: true,
    });

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Spinner size="xl" tone="muted" />
            </div>
        );
    }
    if (error || !data) {
        return (
            <div className="text-muted-foreground p-8 text-center text-sm">
                Eval run not found.
            </div>
        );
    }

    const { run, cases } = data;
    const arms = matrixArms(data.raw_summary);
    const { completeUsd: runCost, knownUsd: knownTrackedCost } =
        resolveRunCosts(data.raw_summary, arms, cases);
    const unknownCostArmCount = resolveUnknownCostArmCount(
        data.raw_summary,
        arms,
    );
    const meanTtft = resolveMeanTtftMs(data.raw_summary, arms, cases);
    const meanTtftLabel =
        meanTtft !== null
            ? formatLatencyMs(meanTtft)
            : run.family === 'prompt_only' && run.summary.total_attempted > 0
              ? 'No answer token'
              : '—';
    return (
        <div className="flex h-full flex-col gap-4 overflow-auto p-4">
            <div>
                <Button variant="ghost" size="sm" asChild={true}>
                    <Link to="/admin/voice-evals">
                        <ArrowLeft className="mr-1 h-4 w-4" />
                        All runs
                    </Link>
                </Button>
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <RunOutcomeBadge run={run} className="text-sm" />
                <EvidenceFamilyBadge family={run.family} />
                <FlakyChip count={run.summary.flaky_cases} />
                <h1 className="font-mono text-lg font-semibold">
                    {run.suite_selector ?? 'Eval run'}
                </h1>
                <span className="text-muted-foreground text-sm">
                    {formatDateTime(
                        run.finished_at ?? run.updated_at ?? run.created_at,
                    )}
                </span>
            </div>
            {run.family === 'prompt_only' && (
                <Card className="border-amber-300 bg-amber-50">
                    <CardContent className="py-3 text-sm text-amber-950">
                        <span className="font-semibold">
                            Prompt-only model benchmark.
                        </span>{' '}
                        This run does not exercise the voice pipeline, deployed
                        service, audio transport, STT, TTS, or telephony, and is
                        not evidence of pipeline or service health.
                    </CardContent>
                </Card>
            )}
            {run.execution_status !== 'succeeded' && (
                <Card
                    className={
                        run.execution_status === 'failed'
                            ? 'border-red-300 bg-red-50'
                            : undefined
                    }
                >
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">
                            {EXECUTION_STATUS_LABELS[run.execution_status]}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1 text-sm">
                        <p>
                            Executor:{' '}
                            {EXECUTION_BACKEND_LABELS[run.execution_backend]}
                            {run.execution_profile &&
                                ` / ${run.execution_profile}`}
                        </p>
                        {run.error_summary ? (
                            <p className="text-red-800">{run.error_summary}</p>
                        ) : (
                            <p className="text-muted-foreground">
                                This page refreshes automatically while the
                                control plane owns the run.
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}
            <div className="grid gap-3 lg:grid-cols-[2fr_3fr]">
                {run.execution_status === 'succeeded' ? (
                    <div className="grid grid-cols-2 gap-3 self-start">
                        <SummaryStat
                            label={arms.length > 0 ? 'Arms' : 'Cases'}
                            value={String(run.summary.total_cases)}
                        />
                        <SummaryStat
                            label="Pass rate"
                            value={formatPercent(run.summary.overall_pass_rate)}
                        />
                        <SummaryStat
                            label="Passed / attempted"
                            value={`${run.summary.total_passed}/${run.summary.total_attempted}`}
                        />
                        <SummaryStat
                            label="Estimated reported model cost"
                            description="Based on recorded provider usage. Excludes unreported calls, speech services, and infrastructure."
                            value={
                                unknownCostArmCount > 0
                                    ? `${formatUsd(knownTrackedCost)} + ${unknownCostArmCount} unknown ${unknownCostArmCount === 1 ? 'arm' : 'arms'}`
                                    : runCost !== null
                                      ? formatUsd(runCost)
                                      : knownTrackedCost !== null
                                        ? `${formatUsd(knownTrackedCost)} (partial; usage missing)`
                                        : 'Unknown'
                            }
                        />
                        <SummaryStat
                            label={`Mean ${ttftMetricLabel(run.family)}`}
                            value={meanTtftLabel}
                        />
                        <SummaryStat
                            label="Flaky / failed"
                            value={`${run.summary.flaky_cases} / ${run.summary.failed_cases}`}
                        />
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-3 self-start">
                        <SummaryStat
                            label="Status"
                            value={
                                EXECUTION_STATUS_LABELS[run.execution_status]
                            }
                        />
                        <SummaryStat
                            label="Executor"
                            value={
                                EXECUTION_BACKEND_LABELS[run.execution_backend]
                            }
                        />
                        <SummaryStat
                            label="Queued"
                            value={formatDateTime(run.queued_at)}
                        />
                        <SummaryStat
                            label="Last update"
                            value={formatDateTime(run.updated_at)}
                        />
                    </div>
                )}
                <RunProvenanceCard run={run} />
            </div>
            <MatrixResultsTable arms={arms} />
            <DataTable
                columns={caseColumns}
                data={[...cases]}
                hidePagination={cases.length <= 50}
                getRowNavigation={(row) => {
                    const routeOptions = {
                        to: '/admin/voice-evals/runs/$runId/cases/$caseResultId',
                        params: { runId, caseResultId: row.id },
                    } as const;
                    return {
                        href: router.buildLocation(routeOptions).href,
                        navigate: () => {
                            void router.navigate(routeOptions);
                        },
                    };
                }}
                renderEmptyState={() => (
                    <div className="text-muted-foreground py-8 text-center text-sm">
                        {run.execution_status === 'succeeded'
                            ? 'This run recorded no case results.'
                            : 'Case results will appear after execution completes.'}
                    </div>
                )}
            />
        </div>
    );
}
